import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tag,
  Plus,
  Check,
  X,
  AlertCircle,
  Search,
  RefreshCw,
  Sparkles,
  BookOpen,
  HelpCircle,
  Link2,
  Trash2,
  ArrowRight,
  Percent,
  ShoppingBag,
  TrendingUp,
  Brain
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CashXTagging() {
  // Auth state
  const [sessionToken, setSessionToken] = useState(null);
  const [account, setAccount] = useState(null);
  
  // Data state
  const [unknownArticles, setUnknownArticles] = useState([]);
  const [knownArticles, setKnownArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState('unknown');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newArticle, setNewArticle] = useState({ name: '', tag: '', aliases: '' });
  const [testMatchResult, setTestMatchResult] = useState(null);
  const [testMatchName, setTestMatchName] = useState('');

  // Load session from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedAccount = localStorage.getItem('cashx_account');
    if (savedToken && savedAccount) {
      setSessionToken(savedToken);
      setAccount(JSON.parse(savedAccount));
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    
    try {
      // Fetch unknown articles
      const unknownRes = await fetch(`${API_BASE}/api/cashx/tagging/unknown?status=pending`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (unknownRes.ok) {
        const data = await unknownRes.json();
        setUnknownArticles(data.articles);
      }
      
      // Fetch known articles
      const knownRes = await fetch(`${API_BASE}/api/cashx/tagging/articles`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (knownRes.ok) {
        const data = await knownRes.json();
        setKnownArticles(data.articles);
      }
      
      // Fetch stats
      const statsRes = await fetch(`${API_BASE}/api/cashx/tagging/stats`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      // Fetch tags
      const tagsRes = await fetch(`${API_BASE}/api/cashx/tagging/tags`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (tagsRes.ok) {
        const data = await tagsRes.json();
        setTags(data.tags);
      }
      
    } catch (error) {
      console.error('Fehler:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken) {
      fetchData();
    }
  }, [sessionToken, fetchData]);

  // Handle tag unknown article
  const handleTagUnknown = async (unknownId, action, tag = null, existingId = null) => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/tagging/unknown/${unknownId}/tag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          action,
          tag,
          existing_article_id: existingId
        })
      });
      
      if (response.ok) {
        toast.success(action === 'confirm_suggestion' ? 'Vorschlag bestätigt!' : 'Artikel getaggt!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Handle create new article
  const handleCreateArticle = async (e) => {
    e.preventDefault();
    
    try {
      const aliases = newArticle.aliases
        ? newArticle.aliases.split(',').map(a => a.trim()).filter(a => a)
        : [];
      
      const response = await fetch(`${API_BASE}/api/cashx/tagging/articles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          name: newArticle.name,
          tag: newArticle.tag,
          aliases: aliases.length > 0 ? aliases : null
        })
      });
      
      if (response.ok) {
        toast.success('Artikel angelegt!');
        setShowCreateDialog(false);
        setNewArticle({ name: '', tag: '', aliases: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  // Test match
  const handleTestMatch = async () => {
    if (!testMatchName.trim()) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/cashx/tagging/test-match?name=${encodeURIComponent(testMatchName)}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTestMatchResult(data);
      }
    } catch (error) {
      toast.error('Fehler beim Testen');
    }
  };

  // Delete article
  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Artikel wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/tagging/articles/${articleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        toast.success('Artikel gelöscht');
        fetchData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Filter known articles
  const filteredKnown = knownArticles.filter(article => {
    const matchesSearch = searchTerm === '' || 
      article.primary_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.names.some(n => n.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTag = selectedTag === 'all' || article.tag === selectedTag;
    return matchesSearch && matchesTag;
  });

  // Get similarity color
  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.85) return 'text-green-400';
    if (similarity >= 0.70) return 'text-yellow-400';
    return 'text-red-400';
  };

  // Login required check
  if (!sessionToken) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Card className="w-full max-w-md bg-gray-900 border-gray-800">
          <CardContent className="p-6 text-center">
            <AlertCircle size={48} className="mx-auto text-amber-400 mb-4" />
            <h2 className="text-xl text-white mb-2">Login erforderlich</h2>
            <p className="text-gray-400 mb-4">Bitte melden Sie sich zuerst an der Kasse an.</p>
            <Button onClick={() => window.location.href = '/kasse'} className="bg-amber-500 text-black">
              Zur Kasse
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="cashx-tagging-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Brain className="text-amber-400" />
              <span className="text-amber-400">Smart</span>
              <span className="text-white">Auto-Tagging</span>
            </h1>
            <p className="text-gray-500 mt-1">
              {account?.business_name} - Automatische Artikelerkennung
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData} className="border-gray-700">
              <RefreshCw size={18} className="mr-2" />
              Aktualisieren
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus size={18} className="mr-2" />
              Artikel anlegen
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Bekannte Artikel</p>
                    <p className="text-2xl font-bold text-white">{stats.known_articles}</p>
                  </div>
                  <BookOpen className="text-blue-400" size={24} />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Unbekannt (offen)</p>
                    <p className="text-2xl font-bold text-amber-400">{stats.unknown_pending}</p>
                  </div>
                  <HelpCircle className="text-amber-400" size={24} />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Auto-erkannt</p>
                    <p className="text-2xl font-bold text-green-400">{stats.total_tagged_orders}</p>
                  </div>
                  <Sparkles className="text-green-400" size={24} />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Erkennungsrate</p>
                    <p className="text-2xl font-bold text-white">{stats.auto_tag_rate}%</p>
                  </div>
                  <TrendingUp className="text-purple-400" size={24} />
                </div>
                <Progress value={stats.auto_tag_rate} className="mt-2 h-1" />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Test Match */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search size={18} />
              Matching testen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="Artikelname eingeben zum Testen..."
                value={testMatchName}
                onChange={(e) => setTestMatchName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTestMatch()}
                className="flex-1 bg-gray-800 border-gray-700"
              />
              <Button onClick={handleTestMatch} className="bg-blue-600 hover:bg-blue-700">
                <Search size={18} className="mr-2" />
                Testen
              </Button>
            </div>
            
            {testMatchResult && (
              <div className="mt-4 p-4 rounded-lg bg-gray-800">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <p className="text-gray-400 text-sm">Eingabe:</p>
                    <p className="text-white font-mono">"{testMatchResult.incoming_name}"</p>
                    <p className="text-gray-500 text-xs">Normalisiert: {testMatchResult.normalized}</p>
                  </div>
                  <ArrowRight className="text-gray-500" />
                  <div className="flex-1">
                    {testMatchResult.match ? (
                      <>
                        <p className="text-gray-400 text-sm">Match:</p>
                        <p className="text-white font-mono">"{testMatchResult.match.primary_name}"</p>
                        <Badge className="mt-1">{testMatchResult.match.tag}</Badge>
                      </>
                    ) : (
                      <p className="text-red-400">Kein Match gefunden</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${getSimilarityColor(testMatchResult.similarity)}`}>
                      {testMatchResult.similarity_percent}
                    </p>
                    <Badge variant="outline" className={
                      testMatchResult.match_type === 'exact' || testMatchResult.match_type === 'auto' 
                        ? 'border-green-500 text-green-400'
                        : testMatchResult.match_type === 'suggest'
                          ? 'border-yellow-500 text-yellow-400'
                          : 'border-red-500 text-red-400'
                    }>
                      {testMatchResult.match_type === 'exact' && '✅ Exakt'}
                      {testMatchResult.match_type === 'auto' && '✅ Auto'}
                      {testMatchResult.match_type === 'suggest' && '⚠️ Vorschlag'}
                      {testMatchResult.match_type === 'unknown' && '❌ Unbekannt'}
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-2 mb-6">
            <TabsTrigger value="unknown" className="flex items-center gap-2">
              <AlertCircle size={16} />
              Unbekannte Artikel
              {unknownArticles.length > 0 && (
                <Badge className="ml-1 bg-amber-500 text-black">{unknownArticles.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="known" className="flex items-center gap-2">
              <BookOpen size={16} />
              Bekannte Artikel
              <Badge variant="outline" className="ml-1">{knownArticles.length}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Unknown Articles Tab */}
          <TabsContent value="unknown">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="animate-spin text-gray-400" size={32} />
              </div>
            ) : unknownArticles.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <Sparkles size={64} className="mx-auto text-green-400 mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">
                    Alles erkannt! 🎉
                  </h2>
                  <p className="text-gray-400">
                    Keine unbekannten Artikel. Das System lernt automatisch.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {unknownArticles.map((article) => (
                  <Card key={article.id} className="bg-gray-900 border-gray-800 border-l-4 border-l-amber-500">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg font-semibold text-white">
                              "{article.name}"
                            </span>
                            <Badge variant="outline" className="border-gray-600">
                              {article.order_count}× bestellt
                            </Badge>
                          </div>
                          
                          {article.suggested_match ? (
                            <div className="p-3 rounded bg-gray-800 mb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-400 mb-1">
                                <Sparkles size={14} />
                                Vorschlag ({Math.round(article.suggested_similarity * 100)}% ähnlich):
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-white font-medium">
                                  "{article.suggested_match.primary_name}"
                                </span>
                                <Badge className="bg-blue-500">{article.suggested_match.tag}</Badge>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm mb-3">
                              Kein ähnlicher Artikel gefunden
                            </p>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          {article.suggested_match && article.suggested_similarity >= 0.70 && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleTagUnknown(article.id, 'confirm_suggestion')}
                            >
                              <Check size={16} className="mr-1" />
                              Bestätigen
                            </Button>
                          )}
                          
                          <Select
                            onValueChange={(tag) => handleTagUnknown(article.id, 'create_new', tag)}
                          >
                            <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700">
                              <SelectValue placeholder="Neu anlegen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {tags.map(t => (
                                <SelectItem key={t.name} value={t.name}>
                                  {t.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Known Articles Tab */}
          <TabsContent value="known">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Bekannte Artikel</CardTitle>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-48 bg-gray-800 border-gray-700"
                    />
                    <Select value={selectedTag} onValueChange={setSelectedTag}>
                      <SelectTrigger className="w-[150px] bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Filter Tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Tags</SelectItem>
                        {tags.map(t => (
                          <SelectItem key={t.name} value={t.name}>
                            {t.name} ({t.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-800">
                        <TableHead className="text-gray-400">Artikel</TableHead>
                        <TableHead className="text-gray-400">Varianten</TableHead>
                        <TableHead className="text-gray-400">Tag</TableHead>
                        <TableHead className="text-gray-400">Bestellungen</TableHead>
                        <TableHead className="text-gray-400 text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredKnown.map((article) => (
                        <TableRow key={article.id} className="border-gray-800">
                          <TableCell>
                            <span className="font-medium text-white">{article.primary_name}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {article.names.filter(n => n !== article.primary_name).slice(0, 3).map((name, i) => (
                                <Badge key={i} variant="outline" className="text-xs border-gray-600">
                                  {name.length > 20 ? name.substring(0, 20) + '...' : name}
                                </Badge>
                              ))}
                              {article.names.length > 4 && (
                                <Badge variant="outline" className="text-xs border-gray-600">
                                  +{article.names.length - 4} mehr
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-blue-500">{article.tag}</Badge>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {article.order_count}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteArticle(article.id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Article Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-gray-900 border-gray-800">
            <DialogHeader>
              <DialogTitle className="text-white">Neuen Artikel anlegen</DialogTitle>
              <DialogDescription className="text-gray-400">
                Erstellen Sie einen neuen bekannten Artikel für das Auto-Tagging.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateArticle} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Artikelname</Label>
                <Input
                  placeholder="z.B. Classic Burger"
                  value={newArticle.name}
                  onChange={(e) => setNewArticle({...newArticle, name: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Kategorie (Tag)</Label>
                <Select
                  value={newArticle.tag}
                  onValueChange={(v) => setNewArticle({...newArticle, tag: v})}
                  required
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Tag auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.map(t => (
                      <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Aliase (optional, kommagetrennt)</Label>
                <Input
                  placeholder="z.B. Classic Burger M, Klassik Burger"
                  value={newArticle.aliases}
                  onChange={(e) => setNewArticle({...newArticle, aliases: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-gray-700">
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black">
                  Anlegen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
